import React from "react";
import {
  View,
  ScrollView,
  KeyboardAvoidingView,
  Dimensions,
  FlatList,
  StyleSheet,
  Platform,
  Text,
} from "react-native";

import {
  hasItems,
  isAndroid,
  isWeb,
  navigate,
  sortByRelevance,
} from "../../../../helpers/utils";
import appStyles from "../../../appStyles";

import Search from "../../common/search-bar/Search";
import IntegrationWrapper from "../details/IntegrationWrapper";
import Header from "../../common/header/index";
import IntegrationsMappingFabButton from "./IntegrationsMappingFabButton";
import ElementsList from "../../elements/list/ElementsList";
import ElementsHelper from "../../elements/ElementsHelper";
import appPages from "../../../../appPages";
import IntegrationsHelper from "../IntegrationsHelper";
import { uniqueId, find } from "lodash";

const { height: HEIGHT } = Dimensions.get("window");

export default class IntegrationsMappingPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    const params = this.props.route.params || {};
    const integrations = params.integrations || [];
    const integrationsCount = integrations.length;

    // Url to redirect to if user hits the back button
    const navState = props.navigation?.dangerouslyGetState() || {};
    const routes = navState.routes.slice(-2);
    let goBackUrl = hasItems(routes) ? routes[0].name : null;

    // TODO: remove after integrations get id from API
    integrations.forEach((int) => {
      if (!int.id) int.id = uniqueId();
    });

    this.state = {
      integrations,
      selectedIntegration: {},
      integrationsCount,
      selectedOperator: "match",
      isSearchActive: true,
      timeslot: params.timeslot,
      returnUrl: params.returnUrl, // url to redirect after map
      goBackUrl, // url to redirect if user hit back button
    };

    this.getElements();

    if (integrationsCount == 1) {
      this._mapSingleIntegration(integrations[0]);
    }
  }

  _mapSingleIntegration = (selectedIntegration) => {
    this.controller.getIntegrationRules().then((allRules) => {
      // If editing existing integration rule
      const currentRule = find(
        allRules,
        (r) => r.id == selectedIntegration.matching_rule,
      );

      const rulesModel = currentRule
        ? IntegrationsHelper.getRuleOptions({
            ...currentRule,
            name: selectedIntegration.website || currentRule.name, // for chrome integrations
          })
        : IntegrationsHelper.getIntegrationRules(selectedIntegration);

      selectedIntegration.rules = rulesModel.rules;
      selectedIntegration.rule = rulesModel.defaultRule;

      this.setState((state) => ({ ...state, selectedIntegration }));
    });
  };

  getElements = async () => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));

    // Sync with server
    this.controller.getApiData("elements", {}, (apiResponse) => {
      const elements = ElementsHelper.sortElements(
        apiResponse,
        "is_major|is_core",
      );

      this.setState((state) => ({
        ...state,
        isLoading: false,
        elements,
      }));
    });
  };

  // ------------------------------------- GO BACK -------------------------------------

  goBack = () => {
    const { selectedIntegration } = this.state;
    const { hasChanged, element } = selectedIntegration || {};

    if (hasChanged && element) {
      return this.mapSelectedElement(element);
    }

    this._returnToPreviousPage();
  };

  _returnToPreviousPage = () => {
    const { selectedIntegration, goBackUrl } = this.state;
    const { id } = selectedIntegration || {};

    if (goBackUrl) {
      navigate(this.controller, goBackUrl, "changePage", { selectedIds: [id] });
    } else {
      this.controller.goBack();
    }
  };

  // ------------------------------------- MAP -------------------------------------
  mapSelectedElement = (element) => {
    const { selectedIntegration } = this.state;

    // Edit rule mapping when coming from Rules Page
    if (selectedIntegration.id == selectedIntegration.matching_rule) {
      this._editRule(element);

      // Normal integration + element mapping
    } else {
      this._saveMapping(element);
    }
  };

  _saveMapping = (element) => {
    const { selectedIntegration } = this.state;
    if (!selectedIntegration || !element) return;

    const { operator, discriminator } = selectedIntegration?.rule || {};
    if (!operator || !discriminator) return;

    IntegrationsHelper.mapIntegrationToElement(
      this.controller,
      {
        operator,
        discriminator,
        integration: selectedIntegration,
        element,
      },
      (response) => {
        this.setState((state) => ({
          ...state,
          selectedIntegration: {
            ...selectedIntegration,
            matching_rule: response?.id,
          },
        }));

        // New rules was created => refresh for history page mapping
        this.controller.getIntegrationRules().then((rules) => {
          this.onConfirmComplete(element, response?.id);
        });
      },
    );
  };

  _editRule = (element) => {
    const { selectedIntegration } = this.state;

    const ruleId = selectedIntegration?.matching_rule;
    if (!element || !ruleId) return;

    IntegrationsHelper.editRule(
      this.controller,
      {
        id: ruleId,
        ...(selectedIntegration.rule || {}),
      },
      element.id,
      () => this.onConfirmComplete(element, ruleId),
    );
  };

  onConfirmComplete = (element, newMatchingRuleId) => {
    const { timeslot, returnUrl, selectedIntegration } = this.state;

    if (returnUrl && (selectedIntegration || element)) {
      const payload = {
        // Coming from Integrations Home => redirect to Rules Page
        selectedIds: [newMatchingRuleId],
        // Track pages
        map_element: element,
        timeslot,
      };

      return this.controller.navigate(returnUrl, payload);
    }

    this.controller.goBack();
  };

  toggleIntegrationSelection = (integration) => {
    this.setState((state) => ({
      ...state,
      selectedIntegration: integration,
    }));
  };

  // ------------------------------------- FILTER -------------------------------------
  filterByKeyword = (filters) => {
    const elements = ElementsHelper.filterResults(this.controller, {
      ...filters,
      sortBy: "name",
    });

    this.setState((state) => ({
      ...state,
      isLoading: false,
      elements: sortByRelevance(elements, this.state?.filters?.term),
      filters,
    }));
  };

  goToIntegrations = () => {
    navigate(this.controller, appPages.integrationsV2);
  };

  toggleSearchFocus = (isFocused) => {
    this.setState((state) => ({
      ...state,
      isSearchActive: isFocused,
    }));
  };

  // ------------------------------------- RENDER -------------------------------------
  render() {
    const {
      integrations,
      elements,
      isLoading,
      integrationsCount,
      selectedIntegration,
      isSearchActive,
    } = this.state;

    const infoText = selectedIntegration?.element
      ? "Replace the Element that best fits the activity above:"
      : "Choose the Element that best fits the activity above:";

    return (
      <View style={[appStyles.container, { backgroundColor: "#F3F2F1" }]}>
        <KeyboardAvoidingView
          style={appStyles.kbViewNoFooter}
          behavior="height"
          keyboardVerticalOffset={isAndroid ? 20 : 0}
        >
          <Header
            title="Associate Data with Element"
            subTitle="Select the integration data and related Element"
            isLoading={isLoading}
            goBack={this.goBack}
          />

          {/* Integrations */}
          {hasItems(integrations) ? (
            <FlatList
              ListHeaderComponent={() =>
                integrationsCount == 1 ? null : (
                  <Text style={styles.sectionText}>Integration Data</Text>
                )
              }
              style={[
                {
                  paddingHorizontal: 0,
                  flexGrow: 0,
                },
                isSearchActive && integrationsCount > 1
                  ? { maxHeight: 100 }
                  : {
                      maxHeight: "30%",
                    },
              ]}
              data={integrations}
              keyExtractor={(_, index) => index}
              renderItem={({ item }) => (
                <IntegrationWrapper
                  data={item}
                  controller={this.controller}
                  hasRules
                  showAssociation
                  showDate
                  useRadioButton
                  showSelector={integrationsCount > 1}
                  isSelected={selectedIntegration?.id == item.id}
                  onToggle={this.toggleIntegrationSelection}
                  onContainerPress={() => this.toggleSearchFocus(false)}
                  style={{ marginBottom: 0, paddingVertical: 4 }}
                />
              )}
            />
          ) : null}

          {/* Separator */}
          <View
            style={{ height: 10, backgroundColor: "#F5F5F5", marginBottom: -2 }}
          />

          {/* Elements */}
          {hasItems(elements) ? (
            <ScrollView
              style={[styles.scrollView, !isWeb && { maxHeight: 300 }]}
              contentContainerStyle={{ paddingBottom: 80 }}
            >
              <ElementsList
                infoText={infoText}
                elements={elements}
                initialNumToRender={10}
                controller={this.controller}
                onElementSelected={this.mapSelectedElement}
                noIcons={true}
                hideActions={true}
                containerStyle={styles.list}
                listStyle={
                  {
                    //maxHeight: HEIGHT - 300,
                  }
                }
              />
            </ScrollView>
          ) : null}

          <View
            style={[appStyles.footerOptions, appStyles.footerOptionsWhite2]}
          >
            <Search
              placeholder="Search Elements or Create New"
              filterResults={this.filterByKeyword}
              controller={this.controller}
              onInputFocus={() => this.toggleSearchFocus(true)}
              autoFocus={false}
            />
          </View>
        </KeyboardAvoidingView>

        <View style={appStyles.footerOptions}>
          <IntegrationsMappingFabButton
            style={{
              bottom: 60,
            }}
          />
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  sectionText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: "#8B92A1",
    marginTop: 8,
    marginLeft: 9,
  },
  scrollView: {
    paddingHorizontal: 0,
    flexGrow: 0,
  },
  list: {
    paddingTop: 8,
    backgroundColor: "#FFF",

    borderTopColor: "#CDCED2",
    borderTopWidth: 0.5,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,

    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: -2 },
    shadowColor: "#F5F5F5",
    shadowRadius: 3,
    elevation: 3,
  },
});
